import React, { useState } from 'react';
import {
  Box, Typography, Chip, Collapse, IconButton, Tooltip,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import WorkOutlineIcon from '@mui/icons-material/WorkOutlined';
import { CandidateResult } from '../types';

const NATIONALITY_FLAGS: Record<string, string> = {
  Egyptian: '🇪🇬', Indian: '🇮🇳', Filipino: '🇵🇭', Pakistani: '🇵🇰',
  Emirati: '🇦🇪', British: '🇬🇧', Lebanese: '🇱🇧', Jordanian: '🇯🇴',
  Syrian: '🇸🇾', Bangladeshi: '🇧🇩', Sudanese: '🇸🇩', Ethiopian: '🇪🇹',
  Nigerian: '🇳🇬', 'South African': '🇿🇦', American: '🇺🇸', Saudi: '🇸🇦',
  Iraqi: '🇮🇶', Iranian: '🇮🇷', Turkish: '🇹🇷', 'Sri Lankan': '🇱🇰',
  Nepali: '🇳🇵', Moroccan: '🇲🇦', Tunisian: '🇹🇳', Algerian: '🇩🇿',
  Canadian: '🇨🇦', Australian: '🇦🇺', German: '🇩🇪', French: '🇫🇷',
  Spanish: '🇪🇸', Italian: '🇮🇹', Chinese: '🇨🇳', Indonesian: '🇮🇩',
  Malaysian: '🇲🇾', Kenyan: '🇰🇪', Ugandan: '🇺🇬', Ghanaian: '🇬🇭',
  Kuwaiti: '🇰🇼', Bahraini: '🇧🇭', Qatari: '🇶🇦', Omani: '🇴🇲',
  Yemeni: '🇾🇪', Libyan: '🇱🇾', Ugandan2: '🇺🇬', Russian: '🇷🇺',
  Ukrainian: '🇺🇦', Portuguese: '🇵🇹', Dutch: '🇳🇱', Belgian: '🇧🇪',
};

function getFlag(nationality: string) {
  return NATIONALITY_FLAGS[nationality] || '🌍';
}

function getScoreClass(score: number | null, isStray: boolean) {
  if (score === null) return 'score-none';
  if (score >= 75) return 'score-high';
  if (score >= 50) return 'score-mid';
  if (score >= 25) return 'score-low';
  return 'score-poor';
}

function getScoreColor(score: number | null) {
  if (score === null) return '#94A3B8';
  if (score >= 75) return '#10B981';
  if (score >= 50) return '#FF8428';
  if (score >= 25) return '#F59E0B';
  return '#EF4444';
}

function getScoreLabel(score: number | null) {
  if (score === null) return null;
  if (score >= 75) return 'Strong Match';
  if (score >= 50) return 'Good Match';
  if (score >= 25) return 'Partial';
  return 'Poor Match';
}

interface Props {
  candidate: CandidateResult;
  rank: number;
}

const CandidateCard: React.FC<Props> = ({ candidate, rank }) => {
  const muiTheme = useMuiTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const [open, setOpen] = useState(false);

  const { name, matchScore, nationality, yearsExperience, currentRole,
    topSkills, strengths, gaps, summary, isStray } = candidate;

  const accentColor = getScoreColor(matchScore);
  const scoreLabel = getScoreLabel(matchScore);

  return (
    <Box sx={{
      display: 'flex',
      borderRadius: 3,
      overflow: 'hidden',
      background: isDark
        ? isStray ? 'rgba(239,68,68,0.05)' : 'rgba(255,255,255,0.04)'
        : isStray ? 'rgba(239,68,68,0.03)' : 'rgba(255,255,255,0.75)',
      border: `1px solid ${isStray
        ? 'rgba(239,68,68,0.2)'
        : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'}`,
      backdropFilter: 'blur(16px)',
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: `0 8px 32px ${accentColor}22` },
    }}>
      {/* Left accent bar */}
      <Box className={`score-accent ${getScoreClass(matchScore, isStray)}`} />

      <Box sx={{ flex: 1, p: { xs: 2, sm: 2.5 } }}>
        {/* Top row */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
          {/* Rank + Score */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Box sx={{
              width: 54, height: 54, borderRadius: '50%',
              border: `3px solid ${accentColor}`,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.9)',
            }}>
              {matchScore !== null ? (
                <>
                  <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, lineHeight: 1, color: accentColor }}>
                    {matchScore}
                  </Typography>
                  <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: accentColor, lineHeight: 1 }}>%</Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94A3B8', textAlign: 'center', lineHeight: 1.2, px: 0.5 }}>
                  Gen
                </Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', fontWeight: 600 }}>
              #{rank}
            </Typography>
          </Box>

          {/* Name + role */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1rem', letterSpacing: '-0.02em', color: 'text.primary' }}>
                {name}
              </Typography>
              {isStray && (
                <Chip label="Stray" size="small" sx={{ fontSize: '0.68rem', height: 20, fontWeight: 800, background: 'rgba(239,68,68,0.12)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.25)' }} />
              )}
              {scoreLabel && !isStray && (
                <Chip label={scoreLabel} size="small" sx={{ fontSize: '0.68rem', height: 20, fontWeight: 700, background: `${accentColor}18`, color: accentColor, border: `1px solid ${accentColor}30` }} />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              {currentRole && currentRole !== '—' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <WorkOutlineIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>{currentRole}</Typography>
                </Box>
              )}
              {yearsExperience && yearsExperience !== '—' && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>· {yearsExperience} yrs exp</Typography>
              )}
            </Box>
          </Box>

          {/* Nationality */}
          <Tooltip title={nationality}>
            <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '1.6rem', lineHeight: 1 }}>{getFlag(nationality)}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', display: 'block', mt: 0.3 }}>{nationality}</Typography>
            </Box>
          </Tooltip>
        </Box>

        {/* Summary */}
        {summary && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, lineHeight: 1.6, fontSize: '0.84rem' }}>
            {summary}
          </Typography>
        )}

        {/* Skills */}
        {topSkills.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
            {topSkills.map(s => (
              <Chip key={s} label={s} size="small" sx={{
                fontSize: '0.75rem', height: 24, fontWeight: 600,
                background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
                border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
              }} />
            ))}
          </Box>
        )}

        {/* Strengths / Gaps toggle */}
        {(strengths.length > 0 || gaps.length > 0) && (
          <>
            <Box
              onClick={() => setOpen(o => !o)}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', width: 'fit-content' }}
            >
              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>
                {open ? 'Hide' : 'Show'} strengths & gaps
              </Typography>
              <IconButton size="small" sx={{ p: 0, color: 'primary.main', transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Box>
            <Collapse in={open}>
              <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                {strengths.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#10B981', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.75, display: 'block' }}>
                      Strengths
                    </Typography>
                    {strengths.map(s => (
                      <Box key={s} sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 14, color: '#10B981', mt: 0.25, flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>{s}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {gaps.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#EF4444', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.75, display: 'block' }}>
                      Gaps
                    </Typography>
                    {gaps.map(g => (
                      <Box key={g} sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
                        <HighlightOffIcon sx={{ fontSize: 14, color: '#EF4444', mt: 0.25, flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>{g}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Collapse>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CandidateCard;
